import React, { memo, useCallback, useMemo, useState } from "react";
import {
  DateHeader,
  IntervalRenderer,
  ReactCalendarTimelineProps,
  SidebarHeader,
  TimelineHeaders,
  TimelineItemBase,
  Timeline as TimelineWithFields,
} from "react-calendar-timeline";

import moment from "moment";

import {
  formatLocalizedMonth,
  getLanguageFromLocalStorage,
  getVerticalLineClassNamesForTime,
  useGetFarmReportingYear,
  useGetFarms,
  useTranslation,
} from "utils";

import { CarbonProgramOutcomesResponse, FieldListField } from "models";

import { ContextMenu, Drawer, Flex, IconButton, Tag } from "components";

import { FieldWithEnrollmentData } from "pages/app/all-field-view/components/AllFieldsTimeline";
import { getTimelineDefaultStartAndEnd } from "pages/app/field-timeline/utils/TimelineUtils";
import { FieldDeletionDrawer } from "pages/app/fields/components/FieldDeletionDrawer";
import { FieldUpload } from "pages/app/fields/components/FieldUpload.tsx";

export type TimelineCalendarWithFieldsProps = ReactCalendarTimelineProps<TimelineItemBase<number>, FieldWithEnrollmentData> & {
  className?: string;
  allFields: FieldListField[];
  topHeaderExtras?: React.ReactNode;
  highlightCurrentYear?: boolean;
  tagText?: string;
  screenSize?: "small" | "medium" | "large";
  farmFieldsCount?: number;
  hasAppliedFilters?: boolean;
  carbonProgramOutcomesResponse?: CarbonProgramOutcomesResponse;
  reportingYear: number;
};
const TimelineCalendarWithFieldsComponent = <T,>({
  className = "",
  highlightCurrentYear,
  topHeaderExtras,
  tagText,
  allFields,
  screenSize,
  farmFieldsCount,
  hasAppliedFilters,
  reportingYear,
  ...props
}: TimelineCalendarWithFieldsProps) => {
  const { t } = useTranslation(["allFieldView"]);
  const { currentFarm } = useGetFarms();
  const carbonProgram = currentFarm?.carbonProgram ?? null;
  const { data: farmReportingYearData } = useGetFarmReportingYear(currentFarm?.id || null);

  const language = getLanguageFromLocalStorage({ forDates: true });

  // set the language
  moment.locale(language);

  const [showFieldContextMenu, setShowFieldContextMenu] = useState<boolean>(false);
  const [showFieldDeletionDrawer, setShowFieldDeletionDrawer] = useState<boolean>(false);
  // open the drawer by default if the farm has no fields
  const [showFieldUploadDrawer, setShowFieldUploadDrawer] = useState<boolean>(allFields.length === 0);

  const firstEntryYear = Math.min(...props.items.filter((item) => item.start_time).map((item) => moment(item.start_time).year()));
  const startLimit = moment(`${firstEntryYear}-01-01`).subtract(3, "years").startOf("year").valueOf();
  const endLimit = moment().add(5, "years").endOf("year").valueOf();

  const handleTimeChange: ReactCalendarTimelineProps<TimelineItemBase<number>>["onTimeChange"] = (visibleTimeStart, visibleTimeEnd, updateScrollCanvas) => {
    if (visibleTimeStart < startLimit) {
      updateScrollCanvas(startLimit, startLimit + (visibleTimeEnd - visibleTimeStart));
    } else if (visibleTimeEnd > endLimit) {
      updateScrollCanvas(endLimit - (visibleTimeEnd - visibleTimeStart), endLimit);
    } else {
      updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
    }
  };

  const timelineStartEnd = useMemo(
    () => getTimelineDefaultStartAndEnd(farmReportingYearData, carbonProgram, screenSize),
    [farmReportingYearData, carbonProgram, screenSize]
  );
  const timelineZoomLevel = timelineStartEnd.end - timelineStartEnd.start;

  return (
    <>
      {/* eslint-disable-next-line */}
      {/* @ts-ignore */}
      <TimelineWithFields
        {...props}
        // hack to force the component to re-render when the "screenSize" prop changes, we need this to automatically
        // update the defaultTimeStart and defaultTimeEnd without needing to switch to a fully controlled state
        key={`${screenSize}-${farmReportingYearData}`}
        className={`timeline-calendar timeline-calendar-with-fields ${className}`}
        lineHeight={50}
        sidebarWidth={300}
        defaultTimeStart={timelineStartEnd.start}
        defaultTimeEnd={timelineStartEnd.end}
        // no zooming in/out allowed
        minZoom={timelineZoomLevel}
        maxZoom={timelineZoomLevel}
        verticalLineClassNamesForTime={getVerticalLineClassNamesForTime(!!highlightCurrentYear, reportingYear, carbonProgram)}
        onTimeChange={handleTimeChange}
      >
        <TimelineHeaders className="timeline-calendar-header">
          <SidebarHeader>
            {useCallback(
              () => (
                <Flex className="field-info-container" justify="space-between">
                  {farmFieldsCount === 0 && !hasAppliedFilters ? (
                    <p className="paragraph-m margin-0">{`${0} ${t("fieldCount")}`}</p>
                  ) : hasAppliedFilters ? (
                    <p className="paragraph-m margin-0">
                      <span className="paragraph-l-bold margin-0">{allFields.length}</span>
                      {`/${farmFieldsCount} ${t("fieldCount")}`}
                    </p>
                  ) : (
                    <p className="paragraph-m margin-0">{`${farmFieldsCount} ${t("fieldCount")}`}</p>
                  )}
                  <ContextMenu
                    items={[
                      {
                        iconName: "klim/plus",
                        label: t("fieldContextMenu.addFields"),
                        onClick: () => setShowFieldUploadDrawer(true),
                      },
                      {
                        iconName: "tabler/trash",
                        label: t("fieldContextMenu.deleteFields"),
                        state: allFields.length ? "warning" : "disabled",
                        onClick: () => setShowFieldDeletionDrawer(true),
                      },
                    ]}
                    placement="right"
                    visible={showFieldContextMenu}
                    iconSize="m-light"
                    onClose={() => setShowFieldContextMenu(false)}
                  ></ContextMenu>
                  <IconButton onClick={() => setShowFieldContextMenu(true)} iconName="klim/plus" size="small" type="tertiary" />
                </Flex>
              ),
              [farmFieldsCount, allFields, t, hasAppliedFilters, showFieldContextMenu]
            )}
          </SidebarHeader>
          <DateHeader
            unit="year"
            className="header-years"
            labelFormat="YYYY"
            // eslint-disable-next-line
            // @ts-ignore
            intervalRenderer={({ getIntervalProps, intervalContext }: IntervalRenderer<T>) => {
              const isCurrentYear = intervalContext.intervalText === reportingYear.toString();
              const { key, ...rest } = getIntervalProps();

              return (
                <div className="header-years content" {...rest} key={key}>
                  <Flex align="center" justify="flex-start" gap="0.5rem">
                    {intervalContext.intervalText}
                    {isCurrentYear && tagText && <Tag colour="yellow" text={tagText} />}
                    {topHeaderExtras}
                  </Flex>
                </div>
              );
            }}
          />
          <DateHeader
            unit="month"
            className="header-months"
            labelFormat="MMMM"
            // eslint-disable-next-line
            // @ts-ignore
            intervalRenderer={({ getIntervalProps, intervalContext }: IntervalRenderer<T>) => {
              const month = intervalContext.interval.startTime.month();
              const isFirstMonthOfYear = month === 0;

              const { key, ...rest } = getIntervalProps();

              return (
                <div className={`content ${isFirstMonthOfYear && "first-month"} `} {...rest} key={key}>
                  <Flex align="center" justify="center">
                    {formatLocalizedMonth(month).slice(0, 3)}
                  </Flex>
                </div>
              );
            }}
          />
        </TimelineHeaders>
      </TimelineWithFields>
      {allFields.length === 0 && !hasAppliedFilters && (
        <Flex className="padding-400" vertical align="center" justify="center" gap="1rem">
          <IconButton iconName="tabler/circle-plus" shape="square" size="large" type="tertiary" onClick={() => setShowFieldUploadDrawer(true)} />
          <Flex vertical align="center" justify="center" gap="0.5rem">
            <h2 className="margin-0" style={{ textAlign: "center" }}>
              {t("noFields.title")}
            </h2>
            <p className="margin-0" style={{ textAlign: "center" }}>
              {t("noFields.description")}
            </p>
          </Flex>
        </Flex>
      )}
      <Drawer visible={showFieldUploadDrawer} title={t("titleUpdateDrawer")} onClose={() => setShowFieldUploadDrawer(false)}>
        <FieldUpload onFinish={() => setShowFieldUploadDrawer(false)} />
      </Drawer>
      <FieldDeletionDrawer
        fields={allFields}
        visible={showFieldDeletionDrawer}
        onClose={() => setShowFieldDeletionDrawer(false)}
        onDelete={() => {}}
        carbonProgramOutcomesResponse={props.carbonProgramOutcomesResponse}
        carbonProgram={carbonProgram}
      />
    </>
  );
};

export const TimelineCalendarWithFields = memo(TimelineCalendarWithFieldsComponent);
